/**
 * Company data from the Hungarian tax number and from a company name. Pure, no imports (type
 * checked and tested by Node in packages/core/test/taxpayer.test.ts, also used by the web app).
 *
 * NAV taxpayer lookup through the Számlázz.hu Számla Agent (docs.szamlazz.hu/agent/querying_taxpayer,
 * checked 2026-11-01): POST multipart/form-data to https://www.szamlazz.hu/szamla/ with the XML file
 * in the form field "action-szamla_agent_taxpayer". The request root is xmltaxpayer
 * (namespace http://www.szamlazz.hu/xmltaxpayer): beallitasok/szamlaagentkulcs, then torzsszam
 * (exactly 8 digits, the first 8 digits of the tax number). The answer is NAV's QueryTaxpayerResponse
 * (namespace http://schemas.nav.gov.hu/OSA/2.0/api, data elements with the ns2 prefix):
 * result/funcCode OK or ERROR (with errorCode and message), taxpayerValidity true or false (false: no
 * taxpayerData), taxpayerData/taxpayerName (and taxpayerShortName when NAV has one),
 * taxNumberDetail/taxpayerId, vatCode (countyCode), taxpayerAddressList/taxpayerAddressItem with
 * taxpayerAddressType (HQ = seat) and taxpayerAddress: countryCode, postalCode, city, streetName,
 * publicPlaceCategory, number (and building, staircase, floor, door, lotNumber in NAV's schema).
 */

export const TAXPAYER_FORM_FIELD = "action-szamla_agent_taxpayer";

/** The first 8 digits of a Hungarian tax number (törzsszám), or null when it has fewer. */
export function taxBase(taxNumber: string | null | undefined): string | null {
  const digits = (taxNumber ?? "").replace(/\D/g, "");
  if (digits.length !== 8 && digits.length !== 11) return null;
  return digits.slice(0, 8);
}

/** 11 digits as 12345678-2-13; anything else is returned trimmed. */
export function formatTaxNumber(taxNumber: string): string {
  const digits = taxNumber.replace(/\D/g, "");
  return digits.length === 11 ? `${digits.slice(0, 8)}-${digits.slice(8, 9)}-${digits.slice(9)}` : taxNumber.trim();
}

const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/** The Agent's taxpayer request. */
export function taxpayerXml(agentKey: string, base: string): string {
  if (!/^\d{8}$/.test(base)) throw new Error("torzsszam must be 8 digits");
  return `<?xml version="1.0" encoding="UTF-8"?>
<xmltaxpayer xmlns="http://www.szamlazz.hu/xmltaxpayer" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:schemaLocation="http://www.szamlazz.hu/xmltaxpayer https://www.szamlazz.hu/szamla/docs/xsds/taxpayer/xmltaxpayer.xsd">
<beallitasok><szamlaagentkulcs>${esc(agentKey)}</szamlaagentkulcs></beallitasok>
<torzsszam>${base}</torzsszam>
</xmltaxpayer>`;
}

/** Company data as the program fills it into a customer. */
export interface TaxpayerData {
  /** The short name when NAV has one (as it is usually written), else the full name. */
  name: string;
  fullName: string;
  /** 12345678-2-13 when NAV gives all parts, else as much as is known. */
  taxNumber: string;
  postalCode: string | null;
  city: string | null;
  addressLine: string | null;
}

export type TaxpayerAnswer =
  | { ok: true; valid: true; data: TaxpayerData }
  | { ok: true; valid: false }
  | { ok: false; code: string; message: string };

const unescape = (s: string) =>
  s.replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&apos;/g, "'").replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n))).replace(/&amp;/g, "&");
/** The text of the first element with this local name (any namespace prefix) in xml. */
function tag(xml: string, name: string): string | null {
  const m = xml.match(new RegExp(`<(?:[\\w-]+:)?${name}(?:\\s[^>]*)?>([\\s\\S]*?)</(?:[\\w-]+:)?${name}>`));
  const v = m ? unescape(m[1]!).trim() : "";
  return v === "" ? null : v;
}
function tags(xml: string, name: string): string[] {
  return [...xml.matchAll(new RegExp(`<(?:[\\w-]+:)?${name}(?:\\s[^>]*)?>([\\s\\S]*?)</(?:[\\w-]+:)?${name}>`, "g"))].map((m) => m[1]!);
}

/** "BUDAPEST" → "Budapest", "HAJDÚ-BIHAR" → "Hajdú-Bihar"; text with lower case letters stays. */
export function tidyCase(s: string | null): string | null {
  if (!s) return s;
  if (s !== s.toUpperCase()) return s;
  return s.toLowerCase().replace(/(^|[\s.-])(\p{L})/gu, (_, p: string, c: string) => p + c.toUpperCase());
}

const withDot = (s: string) => (/[.]$/.test(s) || !/\d$/.test(s) ? s : `${s}.`);

/** The street line of a NAV address: "Záhony utca 7. B ép. 2. em. 5. ajtó". */
export function addressLine(a: string): string | null {
  const street = [tidyCase(tag(a, "streetName")), tag(a, "publicPlaceCategory")?.toLowerCase(), tag(a, "number")].filter(Boolean).join(" ");
  const extra = [
    tag(a, "building") && `${tag(a, "building")} ép.`,
    tag(a, "staircase") && `${tag(a, "staircase")} lh.`,
    tag(a, "floor") && `${withDot(tag(a, "floor")!)} em.`,
    tag(a, "door") && `${withDot(tag(a, "door")!)} ajtó`,
  ].filter(Boolean);
  const line = [street, ...extra].filter(Boolean).join(" ");
  if (line) return line;
  const lot = tag(a, "lotNumber");
  return lot ? `hrsz. ${lot}` : null;
}

/** Reads the Agent's taxpayer answer (Agent errors come in the headers, NAV errors in result). */
export function readTaxpayerAnswer(headers: { get(name: string): string | null }, body: string): TaxpayerAnswer {
  const dec = (s: string | null) => {
    if (!s) return null;
    try {
      return decodeURIComponent(s.replace(/\+/g, " "));
    } catch {
      return s;
    }
  };
  const agentCode = headers.get("szlahu_error_code");
  if (agentCode) return { ok: false, code: agentCode, message: dec(headers.get("szlahu_error")) ?? "" };
  if (!/QueryTaxpayerResponse/.test(body)) return { ok: false, code: "unknown", message: body.slice(0, 200) };
  const result = tag(body, "result") ?? "";
  if (tag(result, "funcCode") !== "OK") return { ok: false, code: tag(result, "errorCode") ?? "ERROR", message: tag(result, "message") ?? "" };
  if (tag(body, "taxpayerValidity") !== "true") return { ok: true, valid: false };
  const data = tag(body, "taxpayerData");
  const fullName = data ? tag(data, "taxpayerName") : null;
  if (!data || !fullName) return { ok: true, valid: false };
  const detail = tag(data, "taxNumberDetail") ?? "";
  const parts = [tag(detail, "taxpayerId"), tag(detail, "vatCode"), tag(detail, "countyCode")];
  const taxNumber = parts.filter(Boolean).join("-");
  const items = tags(data, "taxpayerAddressItem");
  const item = items.find((i) => tag(i, "taxpayerAddressType") === "HQ") ?? items[0];
  const address = item ? tag(item, "taxpayerAddress") ?? "" : "";
  return {
    ok: true,
    valid: true,
    data: {
      name: tag(data, "taxpayerShortName") ?? fullName,
      fullName,
      taxNumber,
      postalCode: address ? tag(address, "postalCode") : null,
      city: address ? tidyCase(tag(address, "city")) : null,
      addressLine: address ? addressLine(address) : null,
    },
  };
}

/**
 * NAV's tax number, completed from the one typed (or found) when NAV leaves parts out:
 * the same törzsszám and, when NAV gives it, the same VAT code.
 */
export function completeTaxNumber(nav: string, typed: string | null | undefined): string {
  const t = (typed ?? "").replace(/\D/g, "");
  const n = nav.replace(/\D/g, "");
  if (n.length === 11) return formatTaxNumber(n);
  if (t.length === 11 && t.startsWith(n)) return formatTaxNumber(t);
  return nav;
}

// ---------------------------------------------------------------------------
// Company or private person (by the legal form in the name)

/** Hungarian legal forms (and a few neighbouring ones) at the end or inside a name. */
const LEGAL_FORM =
  /(^|[\s,.(])(kft|bt|zrt|nyrt|kkt|rt|kht|nkft|nonprofit kft|e\.\s?v|ev|egyéni vállalkozó|egyéni cég|szövetkezet|kv|egyesület|alapítvány|gmbh|ag|s\.?r\.?o|a\.s|sp\.? z o\.?o|s\.?r\.?l|d\.?o\.?o|ltd|korlátolt felelősségű társaság|betéti társaság|zártkörűen működő részvénytársaság|nyilvánosan működő részvénytársaság|közkereseti társaság)(?=$|[\s,.)])/iu;

/** A company or a sole trader (a legal form in the name); private persons get no company lookup. */
export function isCompanyName(name: string | null | undefined): boolean {
  return LEGAL_FORM.test((name ?? "").trim());
}

// ---------------------------------------------------------------------------
// Tax number from a company name: AI web search, then NAV verification

export interface CompanyCandidate {
  name: string;
  taxNumber: string;
  postalCode: string | null;
  city: string | null;
  addressLine: string | null;
  /** Where the AI found it (a public company registry page). */
  source: string | null;
  /** Confirmed by NAV: the name and the address are NAV's. */
  verified: boolean;
}

export const COMPANY_SEARCH_MAX = 3;

export function companySearchPrompt(name: string, city: string | null | undefined): string {
  return `Find the Hungarian tax number (adószám) of this company or sole trader, as written on a vehicle registration certificate:
Name: ${name.slice(0, 200)}
${city ? `City: ${city.slice(0, 80)}\n` : ""}
Search public Hungarian company registry and company information sites (for example e-cegjegyzek.hu, nemzeticegtar.hu, ceginformacio.hu, opten.hu, ceginfo.hu, cegkereso.hu). Use at most 3 searches.
Return only real companies whose name matches; when several match, the ones in the given city first. At most ${COMPANY_SEARCH_MAX}. Never guess or make up a tax number: copy it exactly from the page.
Answer with JSON only, no other text:
{"candidates":[{"name":"<registered name>","taxNumber":"12345678-2-13","address":"<postal code> <city>, <street and number>","source":"<URL of the page>"}]}
If nothing is found: {"candidates":[]}`;
}

/** "1031 Budapest, Záhony utca 7." → its parts (whatever cannot be split goes to the street line). */
export function splitAddress(address: string | null | undefined): { postalCode: string | null; city: string | null; addressLine: string | null } {
  const a = (address ?? "").trim().replace(/\s+/g, " ");
  if (!a) return { postalCode: null, city: null, addressLine: null };
  const m = a.match(/^(?:HU-?)?(\d{4})\.?\s+([^,]+?)\s*,\s*(.+)$/i);
  if (m) return { postalCode: m[1]!, city: m[2]!.trim(), addressLine: m[3]!.trim() };
  return { postalCode: null, city: null, addressLine: a };
}

/** The AI's candidates checked for shape: a tax number of 11 digits, a web source; at most 3, one per törzsszám. */
export function cleanCandidates(raw: unknown): CompanyCandidate[] {
  const list = (raw as { candidates?: unknown })?.candidates;
  if (!Array.isArray(list)) return [];
  const out: CompanyCandidate[] = [];
  const seen = new Set<string>();
  for (const c of list as Record<string, unknown>[]) {
    const name = typeof c?.name === "string" ? c.name.trim().slice(0, 200) : "";
    const digits = typeof c?.taxNumber === "string" ? c.taxNumber.replace(/\D/g, "") : "";
    if (!name || digits.length !== 11) continue;
    const base = digits.slice(0, 8);
    if (seen.has(base)) continue;
    seen.add(base);
    const source = typeof c.source === "string" && /^https?:\/\/[^\s]+$/i.test(c.source.trim()) ? c.source.trim().slice(0, 500) : null;
    const addr = splitAddress(typeof c.address === "string" ? c.address.slice(0, 300) : null);
    out.push({ name, taxNumber: formatTaxNumber(digits), ...addr, source, verified: false });
    if (out.length === COMPANY_SEARCH_MAX) break;
  }
  return out;
}

/**
 * A candidate after its NAV lookup: confirmed (NAV's name and address, the tax number completed),
 * dropped (null: NAV says that tax number is not valid, or its VAT code differs), or left
 * unverified (no answer: no key, or NAV could not be reached).
 */
export function applyVerification(c: CompanyCandidate, nav: TaxpayerAnswer | null): CompanyCandidate | null {
  if (!nav || !nav.ok) return { ...c, verified: false };
  if (!nav.valid) return null;
  const navDigits = nav.data.taxNumber.replace(/\D/g, "");
  const own = c.taxNumber.replace(/\D/g, "");
  if (!own.startsWith(navDigits.slice(0, 9))) return null;
  return {
    name: nav.data.name,
    taxNumber: completeTaxNumber(nav.data.taxNumber, c.taxNumber),
    postalCode: nav.data.postalCode,
    city: nav.data.city,
    addressLine: nav.data.addressLine,
    source: c.source,
    verified: true,
  };
}
